/**
 * HoverPreview — product-video previews on catalog tiles, with a speaker
 * toggle.
 *
 * Cards whose product carries a video in its media render a hidden
 * .product-card__preview-video inside the tile (snippets/product-card.liquid)
 * plus a speaker button, and flag the wrapper with [data-hover-preview].
 *
 * Desktop (hover-capable): resting the pointer on the tile fades the video in
 * and plays it; leaving pauses and rewinds. Playback tries unmuted first —
 * browsers only allow that after the visitor's first click on the page
 * (autoplay policy), so a blocked attempt falls back to muted and the speaker
 * button (visible while previewing) shows the muted glyph; clicking it
 * unmutes. The button also works as a site-wide preference: muting it stores
 * "off" so later previews start silent and stay silent until unmuted again.
 *
 * Touch: hover doesn't exist, so tapping the media itself starts the preview
 * with sound (the tap is the user gesture browsers require); tapping it again
 * stops the preview. The speaker control mirrors that same start/stop behavior.
 */
class HoverPreview {
  constructor() {
    this.active = null;
    // Checked per event, not once at load: convertibles can gain or lose a
    // mouse mid-session, and emulated touch environments resolve this late.
    this.hoverQuery = window.matchMedia('(hover: hover) and (pointer: fine)');
    document.addEventListener('mouseover', this.handleOver.bind(this));
    document.addEventListener('mouseout', this.handleOut.bind(this));
    document.addEventListener('click', this.handleClick.bind(this));
    // A preview hovered before the visitor's first click plays muted (browsers
    // refuse audible playback without a user gesture). The gesture itself
    // grants sticky activation, so promote the playing preview to sound the
    // moment it happens — unless the visitor muted previews on purpose.
    document.addEventListener('pointerdown', this.handleGesture.bind(this), true);
    document.addEventListener('keydown', this.handleGesture.bind(this), true);
  }

  // Site-wide preview-sound preference, toggled via the speaker button.
  get soundOn() {
    try {
      return localStorage.getItem('archive:preview-sound') !== 'off';
    } catch (_error) {
      return true;
    }
  }

  setSoundPref(on) {
    try {
      localStorage.setItem('archive:preview-sound', on ? 'on' : 'off');
    } catch (_error) {
      // Storage blocked — the toggle still works for the current preview.
    }
  }

  handleOver(event) {
    if (!this.hoverQuery.matches) return;
    const wrap = event.target.closest('[data-hover-preview]');
    if (!wrap || wrap === this.active) return;
    this.start(wrap);
  }

  handleOut(event) {
    if (!this.hoverQuery.matches) return;
    const wrap = event.target.closest('[data-hover-preview]');
    if (!wrap) return;
    if (wrap.contains(event.relatedTarget)) return;
    this.stop(wrap);
  }

  handleClick(event) {
    const button = event.target.closest('[data-preview-sound]');
    const trigger = event.target.closest('[data-preview-trigger]');
    if (!button && !trigger) return;

    event.preventDefault();
    const wrap = (button || trigger).closest('[data-hover-preview]');
    const video = wrap && wrap.querySelector('video');
    if (!video) return;

    if (this.hoverQuery.matches) {
      if (trigger) {
        if (this.active !== wrap || video.paused) this.start(wrap);
        return;
      }

      // Desktop: toggle sound on the playing preview and remember the choice.
      const wantSound = video.muted;
      this.setSoundPref(wantSound);
      video.muted = !wantSound;
      if (video.paused) video.play().catch(() => {});
      this.syncIcon(wrap);
      return;
    }

    // Touch: the image and speaker button both start/stop the preview.
    if (this.active === wrap) {
      this.stop(wrap);
    } else {
      if (this.active) this.stop(this.active);
      this.active = wrap;
      wrap.classList.add('is-previewing');
      video.muted = false;
      const attempt = video.play();
      if (attempt && typeof attempt.finally === 'function') {
        attempt
          .catch(() => {
            video.muted = true;
            return video.play().catch(() => {});
          })
          .finally(() => this.syncIcon(wrap));
      } else {
        this.syncIcon(wrap);
      }
    }
  }

  handleGesture(event) {
    if (event.target.closest('[data-preview-sound]')) return;
    if (!this.active || !this.soundOn) return;
    const video = this.active.querySelector('video');
    if (video && !video.paused && video.muted) {
      video.muted = false;
      this.syncIcon(this.active);
    }
  }

  start(wrap) {
    if (this.active && this.active !== wrap) this.stop(this.active);

    const video = wrap.querySelector('video');
    if (!video) return;

    this.active = wrap;
    wrap.classList.add('is-previewing');

    video.muted = !this.soundOn;
    const attempt = video.play();
    if (attempt && typeof attempt.finally === 'function') {
      attempt
        .catch(() => {
          // Audible autoplay refused (no user gesture yet) — retry silent;
          // the speaker button shows muted so one click brings the sound in.
          video.muted = true;
          return video.play().catch(() => {});
        })
        .finally(() => this.syncIcon(wrap));
    } else {
      this.syncIcon(wrap);
    }
  }

  stop(wrap) {
    const video = wrap.querySelector('video');
    if (video) {
      video.pause();
      video.currentTime = 0;
    }

    wrap.classList.remove('is-previewing');
    this.syncIcon(wrap);
    if (this.active === wrap) this.active = null;
  }

  syncIcon(wrap) {
    const video = wrap.querySelector('video');
    const audible = Boolean(video) && !video.paused && !video.muted;
    wrap.classList.toggle('is-sound-on', audible);
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new HoverPreview();
});
